import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const SLEEP_DATE_RANGE_CONFIG = {
  "7D": { type: "DAY", rangeValue: 7 },
  "14D": { type: "DAY", rangeValue: 14 },
  "21D": { type: "DAY", rangeValue: 21 },
};

const SleepTimeline = ({
  sleepsData,
  activeSleep,
  showDateRangeSelector = false,
}) => {
  const { t } = useTranslation();
  const dateRangeOptions = [
    { value: "7D", label: t("charts.1_week") },
    { value: "14D", label: t("charts.2_weeks") },
    { value: "21D", label: t("charts.3_weeks") },
  ];

  const [dateRange, setDateRange] = useState("7D");

  // Calculate average sleep patterns from the data
  const averageSleepPatterns = useMemo(() => {
    if (!sleepsData || sleepsData.length === 0) return [];

    // Filter data based on selected date range
    const now = new Date();
    const cutoffDate = new Date();

    const selectedOption = SLEEP_DATE_RANGE_CONFIG[dateRange];
    if (selectedOption) {
      if (selectedOption.type === "DAY") {
        cutoffDate.setDate(now.getDate() - selectedOption.rangeValue);
      } else if (selectedOption.type === "MONTH") {
        cutoffDate.setMonth(now.getMonth() - selectedOption.rangeValue);
      }
    } else {
      cutoffDate.setMonth(now.getMonth() - 1); // default
    }

    const filteredSleeps = sleepsData.filter((sleep) => {
      const sleepDate = new Date(sleep.start);
      return sleepDate >= cutoffDate;
    });

    // Group sleeps by their start time (rounded to nearest hour)
    const sleepsByHour = {};

    filteredSleeps.forEach((sleep) => {
      if (!sleep.end) return; // Skip active/incomplete sleeps

      const startDate = new Date(sleep.start);
      const endDate = new Date(sleep.end);

      // Get hour of day for start time
      const startHour = startDate.getHours();
      const startMinute = startDate.getMinutes();
      const startTimeInMinutes = startHour * 60 + startMinute;

      // Calculate duration in minutes
      const durationMs = endDate.getTime() - startDate.getTime();
      const durationMinutes = Math.floor(durationMs / 60000);

      // Store sleep info
      if (!sleepsByHour[startHour]) {
        sleepsByHour[startHour] = [];
      }

      sleepsByHour[startHour].push({
        startTimeInMinutes,
        durationMinutes,
        startHour,
        startMinute,
      });
    });

    // Calculate average sleep patterns by clustering similar sleep times
    const clusters = [];

    // Sort hours
    const hours = Object.keys(sleepsByHour)
      .map(Number)
      .sort((a, b) => a - b);

    hours.forEach((hour) => {
      const sleeps = sleepsByHour[hour];

      // Calculate average start time and duration for this hour
      const avgStartMinutes =
        sleeps.reduce((sum, s) => sum + s.startTimeInMinutes, 0) /
        sleeps.length;
      const avgDuration =
        sleeps.reduce((sum, s) => sum + s.durationMinutes, 0) / sleeps.length;

      // Find if this belongs to an existing cluster (within 2 hours)
      let addedToCluster = false;
      for (let cluster of clusters) {
        const timeDiff = Math.abs(cluster.startTimeInMinutes - avgStartMinutes);
        if (timeDiff <= 120) {
          // Within 2 hours
          // Merge into existing cluster
          cluster.sleeps.push(...sleeps);
          cluster.count += sleeps.length;

          // Recalculate cluster averages
          cluster.startTimeInMinutes =
            cluster.sleeps.reduce((sum, s) => sum + s.startTimeInMinutes, 0) /
            cluster.count;
          cluster.durationMinutes =
            cluster.sleeps.reduce((sum, s) => sum + s.durationMinutes, 0) /
            cluster.count;

          addedToCluster = true;
          break;
        }
      }

      if (!addedToCluster) {
        clusters.push({
          startTimeInMinutes: avgStartMinutes,
          durationMinutes: avgDuration,
          count: sleeps.length,
          sleeps: sleeps,
        });
      }
    });

    // Convert clusters to display format and filter by minimum count
    return clusters
      .filter((cluster) => cluster.count >= 5)
      .map((cluster) => {
        const startHour = Math.floor(cluster.startTimeInMinutes / 60);
        const startMinute = Math.round(cluster.startTimeInMinutes % 60);
        const endTimeInMinutes =
          cluster.startTimeInMinutes + cluster.durationMinutes;

        // Handle day overflow correctly
        const totalEndMinutes = Math.round(endTimeInMinutes) % 1440; // Wrap to 24-hour format
        const endHour = Math.floor(totalEndMinutes / 60);
        const endMinute = totalEndMinutes % 60;

        const formatTime = (h, m) => {
          const hour = h % 24;
          return `${hour.toString().padStart(2, "0")}:${m
            .toString()
            .padStart(2, "0")}`;
        };

        return {
          startTime: formatTime(startHour, startMinute),
          endTime: formatTime(endHour, endMinute),
          startTimeInMinutes: cluster.startTimeInMinutes,
          durationMinutes: cluster.durationMinutes,
          count: cluster.count,
        };
      })
      .sort((a, b) => a.startTimeInMinutes - b.startTimeInMinutes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleepsData, dateRange]);

  // Calculate today's sleep sessions
  const todaySleeps = useMemo(() => {
    if (!sleepsData || sleepsData.length === 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayISO = today.toISOString();

    return sleepsData
      .filter((sleep) => {
        if (!sleep.end) return false; // Skip active/incomplete sleeps (handled separately)

        // Include sleeps that started today
        if (sleep.start >= todayISO) return true;

        // Also include sleeps that ended today (overnight sleeps)
        const endDate = new Date(sleep.end);
        return endDate >= today;
      })
      .map((sleep) => {
        const startDate = new Date(sleep.start);
        const endDate = new Date(sleep.end);
        const startTimeInMinutes =
          startDate.getHours() * 60 + startDate.getMinutes();
        const durationMs = endDate.getTime() - startDate.getTime();
        const durationMinutes = Math.floor(durationMs / 60000);

        return {
          startTimeInMinutes,
          durationMinutes,
          isFromPreviousDay: new Date(sleep.start) < today,
        };
      });
  }, [sleepsData]);

  // Helper to convert minutes to position on timeline (0-100%)
  const getPosition = (minutes) => {
    return (minutes / 1440) * 100; // 1440 minutes in 24 hours
  };

  // Generate hour markers
  const hourMarkers = [];
  for (let i = 0; i < 24; i++) {
    hourMarkers.push(i);
  }

  if (averageSleepPatterns.length === 0) {
    return (
      <Paper
        elevation={1}
        sx={{ p: 2, mt: 2, borderRadius: 2, bgcolor: "background.card" }}
      >
        <Typography
          variant="body1"
          align="center"
          color="text.secondary"
        >
          {t("charts.no_sleep_data")}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={10}
      sx={{
        p: 3,
        bgcolor: "background.card",
        mt: 2,
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="h6">{t("charts.avg_sleep")}</Typography>

        {showDateRangeSelector && (
          <FormControl
            size="small"
            sx={{ minWidth: 120 }}
          >
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              sx={{ backgroundColor: "white" }}
            >
              {dateRangeOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Sleep pattern summary - Two columns */}
      <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
        {/* Average Patterns Column */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            {t("charts.avg_pattern")}
          </Typography>
          {(() => {
            const now = new Date();
            const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();

            return averageSleepPatterns.map((pattern, index) => {
              const isOvernightSleep =
                pattern.startTimeInMinutes + pattern.durationMinutes > 1440;

              return (
                <Box
                  key={`pattern-${index}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                    opacity:
                      pattern.startTimeInMinutes < currentTimeInMinutes
                        ? 0.4
                        : 1,
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{ position: "relative", mr: 1, width: 12, height: 12 }}
                  >
                    {index < averageSleepPatterns.length - 1 && (
                      <Box
                        sx={{
                          position: "absolute",
                          left: "5px",
                          top: "12px",
                          width: "2px",
                          height: "calc(100% + 24px)",
                          backgroundColor: "#E0E0E0",
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: isOvernightSleep
                          ? "#5C6BC0"
                          : "#9FA8DA",
                        position: "relative",
                        zIndex: 1,
                      }}
                    />
                  </Box>
                  <Typography variant="body2">
                    {pattern.startTime} - {pattern.endTime}{" "}
                    <Typography
                      component="div"
                      variant="caption"
                      color="text.secondary"
                    >
                      ({Math.floor(pattern.durationMinutes / 60)}h{" "}
                      {Math.round(pattern.durationMinutes % 60)}m, avg.{" "}
                      {pattern.count}x)
                    </Typography>
                  </Typography>
                </Box>
              );
            });
          })()}
        </Box>

        {/* Today's Sleeps Column */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            {t("charts.today")}
          </Typography>
          {(() => {
            const now = new Date();

            // Create combined list for today
            const todayItems = [];

            // Add active sleep
            if (activeSleep) {
              const startDate = new Date(activeSleep.start);
              const hour = startDate.getHours();
              const minute = startDate.getMinutes();
              const timeInMinutes = hour * 60 + minute;

              // Calculate duration
              const durationMs = now.getTime() - startDate.getTime();
              const totalMinutes = Math.floor(durationMs / 60000);
              const durationHours = Math.floor(totalMinutes / 60);
              const durationMins = totalMinutes % 60;
              const durationText =
                durationHours > 0
                  ? `${durationHours}h ${durationMins}m`
                  : `${durationMins}m`;

              todayItems.push({
                type: "active",
                timeInMinutes,
                time: `${hour.toString().padStart(2, "0")}:${minute
                  .toString()
                  .padStart(2, "0")}`,
                duration: durationText,
              });
            }

            // Add today's sleeps
            todaySleeps.forEach((sleep) => {
              const startHour = Math.floor(sleep.startTimeInMinutes / 60);
              const startMinute = Math.round(sleep.startTimeInMinutes % 60);
              const startTimeStr = `${startHour
                .toString()
                .padStart(2, "0")}:${startMinute.toString().padStart(2, "0")}`;

              const totalEndMinutes = Math.round(
                sleep.startTimeInMinutes + sleep.durationMinutes,
              );
              const endHour = Math.floor(totalEndMinutes / 60) % 24;
              const endMinute = totalEndMinutes % 60;
              const endTimeStr = `${endHour
                .toString()
                .padStart(2, "0")}:${endMinute.toString().padStart(2, "0")}`;

              const totalDurationMinutes = Math.round(sleep.durationMinutes);
              const durationHours = Math.floor(totalDurationMinutes / 60);
              const durationMins = totalDurationMinutes % 60;

              todayItems.push({
                type: "today",
                timeInMinutes: sleep.startTimeInMinutes,
                isFromPreviousDay: sleep.isFromPreviousDay,
                startTime: startTimeStr,
                endTime: endTimeStr,
                durationHours,
                durationMins,
              });
            });

            // Sort by time — previous-day sleeps (overnight) first
            todayItems.sort((a, b) => {
              if (a.isFromPreviousDay && !b.isFromPreviousDay) return -1;
              if (!a.isFromPreviousDay && b.isFromPreviousDay) return 1;
              return a.timeInMinutes - b.timeInMinutes;
            });

            return todayItems.map((item, index) => {
              if (item.type === "active") {
                return (
                  <Box
                    key={`active-${index}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        mr: 1,
                        width: 12,
                        height: 12,
                      }}
                    >
                      {index < todayItems.length - 1 && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: "5px",
                            top: "12px",
                            width: "2px",
                            height: "calc(100% + 24px)",
                            backgroundColor: "#E0E0E0",
                          }}
                        />
                      )}
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: "#FFB74D",
                          position: "relative",
                          zIndex: 1,
                          animation: "pulse 2s ease-in-out infinite",
                          "@keyframes pulse": {
                            "0%, 100%": {
                              transform: "scale(1)",
                              boxShadow: "0 0 0 0 rgba(245, 124, 0, 0.7)",
                            },
                            "50%": {
                              transform: "scale(1.1)",
                              boxShadow: "0 0 0 6px rgba(245, 124, 0, 0)",
                            },
                          },
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#F57C00",
                        fontWeight: "bold",
                      }}
                    >
                      {item.time}{" "}
                      <Typography
                        component="div"
                        variant="caption"
                        sx={{ color: "#F57C00" }}
                      >
                        ({item.duration})
                      </Typography>
                    </Typography>
                  </Box>
                );
              } else {
                return (
                  <Box
                    key={`today-${index}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        mr: 1,
                        width: 12,
                        height: 12,
                      }}
                    >
                      {index < todayItems.length - 1 && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: "5px",
                            top: "12px",
                            width: "2px",
                            height: "calc(100% + 24px)",
                            backgroundColor: "#E0E0E0",
                          }}
                        />
                      )}
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "#7E57C2",
                          border: "2px solid #5E35B1",
                          position: "relative",
                          zIndex: 1,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "#5E35B1" }}
                    >
                      {item.startTime} - {item.endTime}{" "}
                      <Typography
                        component="div"
                        variant="caption"
                        color="text.secondary"
                      >
                        ({item.durationHours}h {item.durationMins}m)
                      </Typography>
                    </Typography>
                  </Box>
                );
              }
            });
          })()}
        </Box>
      </Box>

      {/* Visual timeline */}
      <Box sx={{ position: "relative", height: 100, mt: 4 }}>
        {/* Hour markers */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "100%",
            display: "flex",
          }}
        >
          {hourMarkers.map((hour) => (
            <Box
              key={hour}
              sx={{
                flex: 1,
                borderLeft:
                  hour % 6 === 0 ? "2px solid #ddd" : "1px solid #eee",
                position: "relative",
              }}
            >
              {hour % 3 === 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    top: -20,
                    left: -8,
                    fontSize: "0.7rem",
                    color: "text.secondary",
                  }}
                >
                  {hour.toString().padStart(2, "0")}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {/* Sleep bars */}
        {averageSleepPatterns.map((pattern, index) => {
          const startPos = getPosition(pattern.startTimeInMinutes);
          const width = getPosition(pattern.durationMinutes);

          // Handle overnight sleep (crosses midnight)
          if (pattern.startTimeInMinutes + pattern.durationMinutes > 1440) {
            return (
              <React.Fragment key={index}>
                {/* First part (before midnight) */}
                <Box
                  sx={{
                    position: "absolute",
                    left: `${startPos}%`,
                    width: `${getPosition(1440 - pattern.startTimeInMinutes)}%`,
                    height: 40,
                    top: 30,
                    backgroundColor: "#5C6BC0",
                    borderRadius: "4px",
                    opacity: 0.8,
                    transition: "opacity 0.2s",
                    "&:hover": {
                      opacity: 1,
                    },
                  }}
                />
                {/* Second part (after midnight) */}
                <Box
                  sx={{
                    position: "absolute",
                    left: "0%",
                    width: `${getPosition(
                      pattern.startTimeInMinutes +
                        pattern.durationMinutes -
                        1440,
                    )}%`,
                    height: 40,
                    top: 30,
                    backgroundColor: "#5C6BC0",
                    borderRadius: "4px",
                    opacity: 0.8,
                    transition: "opacity 0.2s",
                    "&:hover": {
                      opacity: 1,
                    },
                  }}
                />
              </React.Fragment>
            );
          }

          return (
            <Box
              key={index}
              sx={{
                position: "absolute",
                left: `${startPos}%`,
                width: `${width}%`,
                height: 40,
                top: 30,
                backgroundColor: "#9FA8DA",
                borderRadius: "4px",
                opacity: 0.8,
                transition: "opacity 0.2s",
                "&:hover": {
                  opacity: 1,
                },
              }}
            />
          );
        })}

        {/* Today's sleep sessions */}
        {todaySleeps.map((sleep, index) => {
          const startPos = getPosition(sleep.startTimeInMinutes);
          const width = getPosition(sleep.durationMinutes);

          // Handle overnight sleep (crosses midnight)
          if (sleep.startTimeInMinutes + sleep.durationMinutes > 1440) {
            return (
              <React.Fragment key={`today-${index}`}>
                {/* First part (before midnight) */}
                <Box
                  sx={{
                    position: "absolute",
                    left: `${startPos}%`,
                    width: `${getPosition(1440 - sleep.startTimeInMinutes)}%`,
                    height: 40,
                    top: 30,
                    backgroundColor: "#7E57C2",
                    borderRadius: "4px",
                    opacity: 0.7,
                    border: "2px solid #5E35B1",
                    zIndex: 3,
                  }}
                />
                {/* Second part (after midnight) */}
                <Box
                  sx={{
                    position: "absolute",
                    left: "0%",
                    width: `${getPosition(
                      sleep.startTimeInMinutes + sleep.durationMinutes - 1440,
                    )}%`,
                    height: 40,
                    top: 30,
                    backgroundColor: "#7E57C2",
                    borderRadius: "4px",
                    opacity: 0.7,
                    border: "2px solid #5E35B1",
                    zIndex: 3,
                  }}
                />
              </React.Fragment>
            );
          }

          return (
            <Box
              key={`today-${index}`}
              sx={{
                position: "absolute",
                left: `${startPos}%`,
                width: `${width}%`,
                height: 40,
                top: 30,
                backgroundColor: "#7E57C2",
                borderRadius: "4px",
                opacity: 0.7,
                border: "2px solid #5E35B1",
                zIndex: 3,
              }}
            />
          );
        })}

        {/* Active sleep indicator */}
        {activeSleep &&
          (() => {
            const startDate = new Date(activeSleep.start);
            const startTimeInMinutes =
              startDate.getHours() * 60 + startDate.getMinutes();
            const now = new Date();
            const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();

            // Calculate duration so far
            const durationMinutes =
              currentTimeInMinutes >= startTimeInMinutes
                ? currentTimeInMinutes - startTimeInMinutes
                : 1440 - startTimeInMinutes + currentTimeInMinutes; // Handle crossing midnight

            const startPos = getPosition(startTimeInMinutes);
            const width = getPosition(durationMinutes);

            // Handle overnight sleep
            if (startTimeInMinutes + durationMinutes > 1440) {
              return (
                <React.Fragment>
                  <Box
                    sx={{
                      position: "absolute",
                      left: `${startPos}%`,
                      width: `${getPosition(1440 - startTimeInMinutes)}%`,
                      height: 40,
                      top: 30,
                      backgroundColor: "#FFB74D",
                      borderRadius: "4px",
                      opacity: 0.9,
                      border: "2px solid #F57C00",
                      zIndex: 5,
                      animation: "pulse-bar 2s ease-in-out infinite",
                      "@keyframes pulse-bar": {
                        "0%, 100%": {
                          transform: "scale(1)",
                          boxShadow: "0 0 0 0 rgba(245, 124, 0, 0.7)",
                        },
                        "50%": {
                          transform: "scale(1.02)",
                          boxShadow: "0 0 10px 2px rgba(245, 124, 0, 0.4)",
                        },
                      },
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      left: "0%",
                      width: `${getPosition(
                        startTimeInMinutes + durationMinutes - 1440,
                      )}%`,
                      height: 40,
                      top: 30,
                      backgroundColor: "#FFB74D",
                      borderRadius: "4px",
                      opacity: 0.9,
                      border: "2px solid #F57C00",
                      zIndex: 5,
                      animation: "pulse-bar 2s ease-in-out infinite",
                      "@keyframes pulse-bar": {
                        "0%, 100%": {
                          transform: "scale(1)",
                          boxShadow: "0 0 0 0 rgba(245, 124, 0, 0.7)",
                        },
                        "50%": {
                          transform: "scale(1.02)",
                          boxShadow: "0 0 10px 2px rgba(245, 124, 0, 0.4)",
                        },
                      },
                    }}
                  />
                </React.Fragment>
              );
            }

            return (
              <Box
                sx={{
                  position: "absolute",
                  left: `${startPos}%`,
                  width: `${width}%`,
                  height: 40,
                  top: 30,
                  backgroundColor: "#FFB74D",
                  borderRadius: "4px",
                  opacity: 0.9,
                  border: "2px solid #F57C00",
                  zIndex: 5,
                  animation: "pulse-bar 2s ease-in-out infinite",
                  "@keyframes pulse-bar": {
                    "0%, 100%": {
                      transform: "scale(1)",
                      boxShadow: "0 0 0 0 rgba(245, 124, 0, 0.7)",
                    },
                    "50%": {
                      transform: "scale(1.02)",
                      boxShadow: "0 0 10px 2px rgba(245, 124, 0, 0.4)",
                    },
                  },
                }}
              />
            );
          })()}

        {/* Current time indicator */}
        <Box
          sx={{
            position: "absolute",
            left: `${getPosition(
              new Date().getHours() * 60 + new Date().getMinutes(),
            )}%`,
            top: 25,
            bottom: 0,
            width: 2,
            backgroundColor: "#F57C00",
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -8,
              left: -4,
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#F57C00",
            }}
          />
        </Box>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 3, textAlign: "center" }}
      >
        {t("charts.based_on_sleep", {
          period:
            dateRangeOptions
              .find((opt) => opt.value === dateRange)
              ?.label.toLowerCase() || t("charts.last_months"),
        })}
      </Typography>
    </Paper>
  );
};

export default SleepTimeline;
